import { Component, OnDestroy, OnInit, Renderer2, ViewChild } from "@angular/core";
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { ToastrService } from "ngx-toastr";
import valZip from "val-zip";
import { 
    regexPatterns,
    maxLengths, 
    isInValidator,
    isValidUsZip
} from "../shared/utilities/validators.utility";
import { 
    GRADE_LEVEL_LIST,
    SIGNUP_EXTERNAL_LINKS,
    COUNTRY_OPTIONS
} from "../shared/config/app.constants";
import { InputMask } from "@vg-constellation/angular-16/input";
import { CanadianProvinceData } from "../shared/models/canadian-provinces-data";
import { UsStateData } from "../shared/models/us-state-data";
import { ApiService } from "../shared/services/api.service";
import { ModalService } from "../shared/services/modal.service";
import { Router } from "@angular/router";
import { LanguageService } from "../shared/services/language.service";
import { PATH_TRANSLATIONS, SupportedLang } from '../shared/config/localized-paths';
import { SearchComponent } from '@vg-constellation/angular-16/search';
import { map } from "rxjs";
import { SchoolMetaData } from "../shared/models/school-meta-data";
import { PaperMaterialRequestAttribute } from "../models/material-interface";
import { TranslateService } from "@ngx-translate/core";
import {
    HowDidYouHearAboutUsResponse,
    mapHowDidYouHearAboutUsOptions,
} from "../shared/utilities/how-did-you-hear-about-us.utility";

@Component({
    selector: 'app-order-materials',
    templateUrl: './order-materials.component.html',
    styleUrls: ['./order-materials.component.scss']
})
export class OrderMaterialsComponent implements OnInit, OnDestroy {
    // Form
    orderMaterialsForm: FormGroup;

    // Screen size
    isSmallScreen: boolean;

    // Masks
    integerMask = InputMask.INTEGER;
    domesticPhoneNumberMask = InputMask.PHONE;
    zipCodeMask = {
        mask: 'VALUE',
        lazy: true,
        blocks: {
            VALUE: {
                mask: '00000'
            }
        }
    };

    // Constants
    maxLengths = maxLengths;
    references = {};
    signupExternalLinks = SIGNUP_EXTERNAL_LINKS;
    grades = GRADE_LEVEL_LIST;
    countries = {
        unitedStates: COUNTRY_OPTIONS.unitedStates,
        canada: COUNTRY_OPTIONS.canada
    };
    states = UsStateData;
    provinces = CanadianProvinceData;

    // State
    isSchoolCountryCanada = false;
    isSubmittedAndInvalid = false;
    isSending = false;
    emailSent = false;

    // Utility
    preserveOriginalOrder = () => 0;  // Stops the keyvalue pipe from sorting results on the key``
    private keydownListener: (event: KeyboardEvent) => void;

    // Search school
    enableAddSchoolMode: boolean = false;
    @ViewChild('searchComponent')
    searchComponent!: SearchComponent;
    selectedSchoolMetaData: SchoolMetaData | null = null;
    initialInputValue: string = '';
    selectedSchool: string = '';

    //School Search
    triggeredSchoolSearch: Boolean = false;
    schools = [];
    // gradeKitOrder index
    gradeKitOrderLangIndex = 0;


    constructor(
        private readonly toast: ToastrService,
        private readonly _apiService: ApiService,
        private readonly breakpointObserver: BreakpointObserver,
        private readonly router: Router,
        private readonly modalService: ModalService,
        private readonly languageService: LanguageService,
        private readonly translateService: TranslateService,
        private readonly renderer: Renderer2,
        public readonly fb: FormBuilder
    ) {
        this.keydownListener = this.handleKeydown.bind(this);
    }

    ngOnInit(): void {
        this.languageService.initialize(this.renderer);
        delete this.grades.grade_other;
        this.getHowDidYouHearAboutUs();
        this.initForm();
        this.breakpointObserver
            .observe(['(max-width: 768px)'])
            .subscribe((state: BreakpointState) => {
                this.isSmallScreen = state.matches;
        });
        document.addEventListener('keydown', this.keydownListener);
    }

    initForm(): void {
        this.orderMaterialsForm = this.fb.group(
            {
                firstName: [
                    '', 
                    Validators.compose([
                        Validators.required,
                        Validators.maxLength(this.maxLengths.firstName),
                        Validators.pattern(regexPatterns.name)
                    ])
                ],
                lastName: [
                    '', 
                    Validators.compose([
                        Validators.required,
                        Validators.maxLength(this.maxLengths.lastName),
                        Validators.pattern(regexPatterns.name)
                    ])
                ],
                email: [
                    '', 
                    Validators.compose([
                        Validators.required,
                        Validators.maxLength(this.maxLengths.email),
                        Validators.pattern(regexPatterns.email)
                    ])
                ],
                schoolcountryregion: [this.countries.unitedStates, ''],
                schoolName: ['', ''],
                schoolstreetaddress:  ['', ''],
                schoolsecondaryaddressunit:  ['', ''],
                schoolcity:  ['', ''],
                schoolstate:  ['', ''],
                schoolZipCode: ['', ''],
                schoolMetaDataId: ['', Validators.required],
                gradeKitOrders: this.fb.array([this.createGradeKitFormGroup()]),
                country: [
                    this.countries.unitedStates, 
                    Validators.compose([
                        Validators.required
                    ])
                ],
                shippingAddress: this.fb.group({}),
                phoneNumber: [
                    '', 
                    Validators.compose([
                        Validators.required,
                        Validators.pattern(regexPatterns.domesticPhoneNumber)
                    ])
                ],
                reference: [
                    '', 
                    Validators.compose([
                        Validators.required
                    ]),
                ],
                isTermsAndConditionsAccepted: [
                    false, 
                    Validators.compose([
                        Validators.requiredTrue
                    ])
                ],
                isPrivacyPolicyAccepted: [
                    false, 
                    Validators.compose([
                        Validators.requiredTrue
                    ])
                ],
                captchaResponse: [
                    '',
                    Validators.compose([
                        Validators.required
                    ])
                ]
            }
        );

        this.onShippingAddressCountryChange({value: this.countries.unitedStates});
    }

    createGradeKitFormGroup(): FormGroup {
        const gradeFormGroup = this.fb.group({
            tempId: [this.gradeKitOrderLangIndex],
            gradeLevel: [
                '', 
                Validators.required
            ],
            numberOfStudents: [
                '', 
                Validators.compose([
                    Validators.required,
                    Validators.min(1),
                    Validators.max(this.maxLengths.numberOfStudents),
                ])
            ],
            [`language_${this.gradeKitOrderLangIndex}`]: [
                'English', 
                Validators.required
            ]
        });

        this.gradeKitOrderLangIndex++;
        return gradeFormGroup;
    }

    createUsAddressFormGroup(): FormGroup {
        return this.fb.group({
            streetAddress: [
                '',
                Validators.compose([
                    Validators.required,
                    Validators.maxLength(this.maxLengths.streetAddress),
                    Validators.pattern(regexPatterns.name)
                ])
            ],
            secondaryAddressUnit: [
                '',
                Validators.compose([
                    Validators.maxLength(this.maxLengths.secondaryAddressUnit),
                    Validators.pattern(regexPatterns.name)
                ])
            ],
            city: [
                '',
                Validators.compose([
                    Validators.required,
                    Validators.maxLength(this.maxLengths.city),
                    Validators.pattern(regexPatterns.name)
                ])
            ],
            state: [
                '',
                Validators.compose([
                    Validators.required,
                    isInValidator(this.states.map(state => state.stateCode))
                ])
            ],
            postalCode: [
                '',
                Validators.compose([
                    Validators.required,
                    isValidUsZip()
                ])
            ]
        });
    }

    createCanadaAddressFormGroup(): FormGroup {
        return this.fb.group({
            streetAddress: [
                '',
                Validators.compose([
                    Validators.required,
                    Validators.maxLength(this.maxLengths.streetAddress),
                    Validators.pattern(regexPatterns.name)
                ])
            ],
            secondaryAddressUnit: [
                '',
                Validators.compose([
                    Validators.maxLength(this.maxLengths.secondaryAddressUnit),
                    Validators.pattern(regexPatterns.name)
                ])
            ],
            city: [
                '',
                Validators.compose([
                    Validators.required,
                    Validators.maxLength(this.maxLengths.city),
                    Validators.pattern(regexPatterns.name)
                ])
            ],
            province: [
                '',
                Validators.compose([
                    Validators.required,
                    isInValidator(this.provinces.map(province => province.canadianProvinceCode))
                ])
            ],
            postalCode: [
                '',
                Validators.compose([
                    Validators.required,
                    Validators.pattern(regexPatterns.zipCanada)
                ])
            ]
        });
    }

    get gradeKitOrders() {
        return this.orderMaterialsForm.get('gradeKitOrders') as FormArray;
    }

    get shippingAddress() {
        return this.orderMaterialsForm.get('shippingAddress') as FormGroup;
    }

    focusNewGradeKitSelectInput(newTempId: string) {
        setTimeout(() => {
            const select = document.getElementById(`gradeLevel_${newTempId}`) as HTMLSelectElement | null;
            if (select) {
                select.focus();
            }
        });
    }

    onSchoolSelect(school: SchoolMetaData): void {
        this.selectedSchoolMetaData = school;
        this.patchSchoolMetaId(school.schoolMetaDataId);
    }

    patchSchoolMetaId(schoolMetaId: string): void {
        this.orderMaterialsForm.patchValue({
            schoolMetaDataId: schoolMetaId
        });
    }

    onManualAdd(): void {
        this.enableAddSchoolMode = !this.enableAddSchoolMode;
        this.addSchoolFormFieldsValidation();
    }

    returnToSearch(): void {
        this.enableAddSchoolMode = !this.enableAddSchoolMode;
        this.onSearchNewSchool();
        this.removeSchoolFormFieldsValidation();
    }

    onSearchNewSchool(): void {
        this.triggeredSchoolSearch = false;
        this.selectedSchoolMetaData = null
        this.patchSchoolMetaId('');
    }

    trackBySchoolId(_: number, school:SchoolMetaData): string {
        return school.schoolMetaDataId;
    }

    addSchoolFormFieldsValidation() {
        const schoolFieldsArray = ['schoolName', 'schoolstreetaddress', 'schoolsecondaryaddressunit', 'schoolcity', 'schoolstate'];
        schoolFieldsArray.forEach((formField) => {
          if(formField==='schoolName'){
            this.orderMaterialsForm.controls[formField].setValidators([
                Validators.required, 
                Validators.maxLength(this.maxLengths.schoolName),
                Validators.pattern(regexPatterns.name)
            ]);
          } else if (formField==='schoolstreetaddress') {
            this.orderMaterialsForm.controls[formField].setValidators([
                Validators.required, 
                Validators.maxLength(this.maxLengths.streetAddress),
                Validators.pattern(regexPatterns.name)
            ]);
          } else if (formField==='schoolsecondaryaddressunit') {
            this.orderMaterialsForm.controls[formField].setValidators([
                Validators.maxLength(this.maxLengths.secondaryAddressUnit),
                Validators.pattern(regexPatterns.name)
            ]);
          } else if (formField==='schoolcity') {
            this.orderMaterialsForm.controls[formField].setValidators([
                Validators.required, 
                Validators.maxLength(this.maxLengths.city),
                Validators.pattern(regexPatterns.name)
            ]);
          } else {
            this.orderMaterialsForm.controls[formField].setValidators([Validators.required]);
          }
          this.orderMaterialsForm.controls[formField].updateValueAndValidity();
        });
        this.orderMaterialsForm.controls['schoolZipCode'].setValidators(this.isValidZipSchool());
        this.orderMaterialsForm.controls['schoolZipCode'].addValidators(Validators.required);
        this.orderMaterialsForm.controls['schoolZipCode'].updateValueAndValidity();
    
        this.orderMaterialsForm.controls['schoolMetaDataId'].clearValidators();
        this.orderMaterialsForm.controls['schoolMetaDataId'].updateValueAndValidity();

        this.onSchoolAddressCountryChange({value: this.orderMaterialsForm.value.schoolcountryregion});
    }
    
    removeSchoolFormFieldsValidation() {
        const schoolFieldsArray = ['schoolName', 'schoolstreetaddress', 'schoolsecondaryaddressunit', 'schoolcity', 'schoolstate'];
        schoolFieldsArray.forEach((formField) => {
            this.orderMaterialsForm.controls[formField].clearValidators();
            this.orderMaterialsForm.controls[formField].updateValueAndValidity();
            this.orderMaterialsForm.controls[formField].setValue('');
        });
        this.orderMaterialsForm.controls['schoolZipCode'].clearValidators();
        this.orderMaterialsForm.controls['schoolZipCode'].updateValueAndValidity();
    
        this.orderMaterialsForm.controls['schoolMetaDataId'].addValidators(Validators.required);
        this.orderMaterialsForm.controls['schoolMetaDataId'].updateValueAndValidity();
    }

    isValidZipSchool(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;
        
            if (value.length === 0) {
            return null
            }

            if (this.isSchoolCountryCanada) {
            return value.match(regexPatterns.zipCanada) ? null : { inValidZip: true };
            } else {
            const isValidUSZip = valZip(value, 'US') && value.match(regexPatterns.zipUsFormat);
            return isValidUSZip ? null : { inValidZip: true };
            }
        };
    }

    onSearchAction(event) {
        switch (event.searchEvent) {
          case 'input': {
            this.triggeredSchoolSearch = false
            break;
          }
          case 'submit': {
            this._apiService.getSchoolMetaData(event.searchTerm).pipe(
              map((res) => {
                return res.schoolMetaDataList.map(school => ({
                  schoolMetaDataId: school.schoolMetaDataId,
                  schoolName: school.schoolName,
                  zipCode: school.zipCode
                }));
              })
            ).subscribe({
              next: (schools) => {
                this.schools = schools;
                this.triggeredSchoolSearch = true
              },
              error: (_) => {
              }
            });
            break;
          }
          case 'clear': {
            this.orderMaterialsForm.controls['schoolName'].setValue('');
            this.selectedSchool = '';
            this.triggeredSchoolSearch = false;
            break;
          }
        }
      }

    onAddRow(): void {
        const newFormGroup = this.createGradeKitFormGroup();
        this.gradeKitOrders.push(newFormGroup);
        const newTempId = newFormGroup.get('tempId')?.value;
        this.focusNewGradeKitSelectInput(newTempId);
    }

    onRemoveRow(rowIndex: number): void {
        this.gradeKitOrders.removeAt(rowIndex);
    }

    onSchoolAddressCountryChange(event): void {
        this.isSchoolCountryCanada = event.value === this.countries.canada;
        if (!this.isSchoolCountryCanada) {
            this.orderMaterialsForm.setControl('schoolstate', this.fb.control(
                '',
                Validators.compose([
                    Validators.required,
                    isInValidator(this.states.map(state => state.stateCode))
                ])
            ));
        } else {  // Must be Canada
            this.orderMaterialsForm.setControl('schoolstate', this.fb.control(
                '',
                Validators.compose([
                    Validators.required,
                    isInValidator(this.provinces.map(province => province.canadianProvinceCode))
                ])
            ));
        }
    }

    onShippingAddressCountryChange(event): void {
        if (event.value === this.countries.unitedStates) {
            this.orderMaterialsForm.setControl('shippingAddress', this.createUsAddressFormGroup());
        } else {  // Must be Canada
            this.orderMaterialsForm.setControl('shippingAddress', this.createCanadaAddressFormGroup());
        }
    }

    showErrorMsg(): void {
        this.languageService.getTranslation(['errorToaster.message', 'errorToaster.title'])
            .subscribe(translations => {
                this.toast.show(
                translations['errorToaster.message'],
                translations['errorToaster.title'],
                { toastClass: 'error' }
                );
            });
    }

    renameLanguageFieldsForApi() {
        const formData = this.orderMaterialsForm.value;

        const transformedGradeKitOrders = formData.gradeKitOrders.map((order: any) => {
            const transformedOrder = { ...order };
            Object.keys(transformedOrder).forEach((key) => {
                if (key.startsWith('language_')) {
                    transformedOrder['language'] = transformedOrder[key];
                    delete transformedOrder[key];
                }
                delete transformedOrder.tempId;
            });
            return transformedOrder;
        });

        const transformedData = {
            ...formData,
            gradeKitOrders: transformedGradeKitOrders
        };

        return transformedData;
    }

    onSubmit(): void {
        this.isSubmittedAndInvalid = this.orderMaterialsForm.invalid;
        if (!this.orderMaterialsForm.invalid) {
            this.isSending = true;
            
            if(this.enableAddSchoolMode){
                const schoolObj = [{
                  schoolName: this.orderMaterialsForm.value.schoolName,
                  country: this.orderMaterialsForm.value.schoolcountryregion,
                  streetAddress1: this.orderMaterialsForm.value.schoolstreetaddress,
                  streetAddress2: this.orderMaterialsForm.value.schoolsecondaryaddressunit,
                  city: this.orderMaterialsForm.value.schoolcity,
                  state:this.orderMaterialsForm.value.schoolstate,
                  zipCode: this.orderMaterialsForm.value.schoolZipCode,
                }];
                this._apiService.createSchoolMetaData(schoolObj).subscribe({
                    next: (schoolObjResponse) => {
                        if (schoolObjResponse.error || schoolObjResponse.code) {
                            this.showErrorMsg();
                            this.isSending = false;
                        } else if (schoolObjResponse && schoolObjResponse[0].schoolMetaDataId) {
                            this.orderMaterialsForm.patchValue({
                                schoolMetaDataId: schoolObjResponse[0].schoolMetaDataId
                            });
                            this.selectedSchoolMetaData = schoolObjResponse[0];
                            this.callCreatePaperMaterial();
                        } else {
                            this.showErrorMsg();
                            this.isSending = false;
                        }
                    },
                    error: (_) => {
                        this.showErrorMsg();
                        this.isSending = false;
                    }
                });
            } else {
                this.callCreatePaperMaterial();
            }
        }
    }

    createOrderRequestObject(): PaperMaterialRequestAttribute {
        const updatedFormData = this.renameLanguageFieldsForApi();

        let orderRequestObject: PaperMaterialRequestAttribute = {
            firstName: updatedFormData.firstName,
            lastName: updatedFormData.lastName,
            email: updatedFormData.email,
            schoolMetaDataId: this.selectedSchoolMetaData?.schoolMetaDataId || updatedFormData.schoolMetaDataId,
            schoolName: this.selectedSchoolMetaData?.schoolName || updatedFormData.schoolName,
            schoolZipCode: this.selectedSchoolMetaData?.zipCode || updatedFormData.schoolZipCode,
            gradeKitOrders: updatedFormData.gradeKitOrders,
            country: updatedFormData.country,
            shippingAddress: updatedFormData.shippingAddress,
            phoneNumber: updatedFormData.phoneNumber,
            reference: updatedFormData.reference,
            isTermsAndConditionsAccepted: updatedFormData.isTermsAndConditionsAccepted,
            isPrivacyPolicyAccepted: updatedFormData.isPrivacyPolicyAccepted,
            captchaResponse: updatedFormData.captchaResponse
        };

        if (orderRequestObject.shippingAddress.secondaryAddressUnit === '') {
            delete orderRequestObject.shippingAddress.secondaryAddressUnit;
        }

        return orderRequestObject;
    }

    callCreatePaperMaterial() {
        let createPaperMaterialRequest: PaperMaterialRequestAttribute = this.createOrderRequestObject();
        this._apiService.createPaperMaterial(createPaperMaterialRequest).subscribe({
            next: response => {
                if (response.error) {
                    this.showErrorMsg();
                    this.isSending = false;
                } else if (response.code && response.message) {
                    this.showErrorMsg();
                    this.isSending = false;
                } else {
                    this.isSending = false;
                    const currentLang = (document.documentElement.lang || 'en') as SupportedLang;
                    const materialsPath = PATH_TRANSLATIONS[currentLang]?.materials ?? 'materials';
                    this.router.navigate(['/', currentLang, materialsPath]).then(() => {
                        this.modalService.openThankYouModal();
                        this.languageService.applyLanguageVisibility(currentLang);
                    });
                }
            },
            error: () => {
                this.showErrorMsg();
                this.isSending = false;
            }
        });
    }

    getHowDidYouHearAboutUs(): void {
        const locale = this.translateService.getCurrentLang() || this.translateService.getFallbackLang() || 'en';
        this._apiService.getHowDidYouHearAboutUs(locale).subscribe({
            next: (response: HowDidYouHearAboutUsResponse) => {
                this.references = mapHowDidYouHearAboutUsOptions(response);
            },
            error: (_) => {
                this.references = {};
            },
        });
    }

    ngOnDestroy(): void {
        document.removeEventListener('keydown', this.keydownListener);
    }

    private handleKeydown(event: KeyboardEvent): void {
        if (event.shiftKey && event.key === 'Tab') {
          const focusedElement = document.activeElement;
          if (focusedElement) {
            focusedElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        }
      }
}
